/** Pure helpers for the Tier 1 layer tree. The document is a flat
 *  map of `LayerNode`s keyed by id, with tree shape carried by
 *  `parentId` / `childIds` and root order on the document's
 *  `rootIds` array.
 *
 *  Every mutating helper returns a NEW `EditorDocument` — the
 *  reducer relies on referential change to know when to re-render.
 *  Helpers never mutate the input doc or its nested objects. */

import type { EditorDocument, LayerNode, LayerMask } from '../types';

/** Empty document — no layers, no roots. The reducer's initial
 *  state uses this until the first layer is added (typically when
 *  a bg image loads). */
export function emptyDocument(): EditorDocument {
  return { rootIds: [], layers: {} };
}

/** Build a document from an ordered flat list (Tier 0 layout —
 *  used by the IndexedDB migration to promote pre-Tier-1 sessions
 *  and by tests). All nodes land at the root with `parentId: null`
 *  in the order given. */
export function fromFlatLayers(layers: LayerNode[]): EditorDocument {
  const map: Record<string, LayerNode> = {};
  const rootIds: string[] = [];
  for (const l of layers) {
    map[l.id] = { ...l, parentId: null };
    rootIds.push(l.id);
  }
  return { rootIds, layers: map };
}

/** Convenience: get a node by id, or null if missing. */
export function getNode(doc: EditorDocument, id: string): LayerNode | null {
  return doc.layers[id] ?? null;
}

/** Walk the tree depth-first in render order (top-most root first,
 *  groups expanded inline before the next sibling). Yields a
 *  `{ node, depth }` pair so consumers can render the LayersPanel
 *  with consistent indentation. Skips dangling references. */
export function* traverseDepthFirst(
  doc: EditorDocument,
): Generator<{ node: LayerNode; depth: number }, void, void> {
  const stack: { id: string; depth: number }[] = [];
  for (let i = doc.rootIds.length - 1; i >= 0; i--) {
    stack.push({ id: doc.rootIds[i], depth: 0 });
  }
  while (stack.length > 0) {
    const { id, depth } = stack.pop()!;
    const node = doc.layers[id];
    if (!node) continue;
    yield { node, depth };
    if (node.type === 'group' && node.childIds && node.childIds.length > 0) {
      // Push children in reverse so the top-most child is yielded first.
      for (let i = node.childIds.length - 1; i >= 0; i--) {
        stack.push({ id: node.childIds[i], depth: depth + 1 });
      }
    }
  }
}

/** Flatten the tree to an ordered array (front-most first), the
 *  shape Tier 0 consumers expect. The depth tag is dropped. Used
 *  while consumers are migrated incrementally. */
export function toOrderedList(doc: EditorDocument): LayerNode[] {
  const out: LayerNode[] = [];
  for (const { node } of traverseDepthFirst(doc)) {
    out.push(node);
  }
  return out;
}

/** Walk a layer's descendants depth-first (front-most child first).
 *  Yields the starting node itself first, then each descendant.
 *  For a non-group node yields just that node. */
export function* traverseDescendants(
  doc: EditorDocument,
  rootId: string,
): Generator<LayerNode, void, void> {
  const stack: string[] = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    const node = doc.layers[id];
    if (!node) continue;
    yield node;
    if (node.type === 'group' && node.childIds && node.childIds.length > 0) {
      for (let i = node.childIds.length - 1; i >= 0; i--) {
        stack.push(node.childIds[i]);
      }
    }
  }
}

/** Returns true when the layer (or any of its descendants, if it's
 *  a group) has rasterizable pixels — i.e. an `image` or `paint`
 *  node. Used to disable the Adjust button when the active layer
 *  is text/shape/adjustment/empty/empty-group (nothing to write
 *  pixels into). Must stay aligned with `getActiveAdjustmentTargets`
 *  in `useFabricCanvas` — both treat the same set of node types
 *  as adjustable. `empty` is excluded because the paint surface
 *  isn't created until first stroke, and adjusting a transparent
 *  surface is a visual no-op; surfacing the disabled state up-front
 *  is more honest than letting the user click and see nothing. */
export function hasRasterizableTarget(doc: EditorDocument, layerId: string | null): boolean {
  if (!layerId) return false;
  const node = doc.layers[layerId];
  if (!node) return false;
  for (const descendant of traverseDescendants(doc, layerId)) {
    if (descendant.type === 'image' || descendant.type === 'paint') {
      return true;
    }
  }
  return false;
}

/** Read the ordered list of children for a layer. Returns the
 *  document's `rootIds` for `parentId: null`. Returns [] for a
 *  non-group layer. */
export function getSiblingOrder(doc: EditorDocument, parentId: string | null): string[] {
  if (parentId === null) return doc.rootIds;
  const parent = doc.layers[parentId];
  if (!parent || parent.type !== 'group') return [];
  return parent.childIds ?? [];
}

interface AddOptions {
  /** id of the parent group, or null for root. Defaults to root. */
  parentId?: string | null;
  /** Index within the parent's child list to insert at. Defaults
   *  to 0 (front-most). */
  index?: number;
}

/** Add a single node to the document. The node's `parentId` /
 *  `childIds` are reset to match the placement options — callers
 *  shouldn't pre-set these. */
export function addNode(
  doc: EditorDocument,
  node: LayerNode,
  opts: AddOptions = {},
): EditorDocument {
  const parentId = opts.parentId ?? null;
  const index = opts.index ?? 0;

  if (doc.layers[node.id]) {
    // Updating an existing id is a logical bug — surface in dev.
    throw new Error(`addNode: layer id "${node.id}" already exists`);
  }
  const placed: LayerNode = {
    ...node,
    parentId,
    // Groups MUST own their childIds; non-groups MUST NOT carry one.
    childIds: node.type === 'group' ? (node.childIds ?? []) : undefined,
  };

  const layers = { ...doc.layers, [node.id]: placed };

  if (parentId === null) {
    const rootIds = insertAt(doc.rootIds, node.id, index);
    return { rootIds, layers };
  }

  const parent = doc.layers[parentId];
  if (!parent || parent.type !== 'group') {
    throw new Error(`addNode: parent "${parentId}" is not a group`);
  }
  const updatedParent: LayerNode = {
    ...parent,
    childIds: insertAt(parent.childIds ?? [], node.id, index),
  };
  return {
    rootIds: doc.rootIds,
    layers: { ...layers, [parentId]: updatedParent },
  };
}

/** Remove a node and all its descendants. Returns a fresh document
 *  with affected parents updated. No-op if the id doesn't exist. */
export function removeNode(doc: EditorDocument, id: string): EditorDocument {
  const node = doc.layers[id];
  if (!node) return doc;

  const removeIds = collectDescendantIds(doc, id);
  removeIds.add(id);

  const layers: Record<string, LayerNode> = {};
  for (const [k, v] of Object.entries(doc.layers)) {
    if (removeIds.has(k)) continue;
    if (v.type === 'group' && v.childIds && v.childIds.some((c) => removeIds.has(c))) {
      layers[k] = { ...v, childIds: v.childIds.filter((c) => !removeIds.has(c)) };
    } else {
      layers[k] = v;
    }
  }

  const rootIds = doc.rootIds.filter((rid) => !removeIds.has(rid));
  return { rootIds, layers };
}

interface MoveOptions {
  /** New parent id, or null for the document root. */
  parentId: string | null;
  /** Index within the new parent's child list. */
  index: number;
}

/** Move an existing node to a new (parentId, index). Validates
 *  against cyclic parenting (can't move a group into its own
 *  descendant) — throws on violation rather than silently no-op
 *  so the bug surfaces in tests. */
export function moveNode(doc: EditorDocument, id: string, opts: MoveOptions): EditorDocument {
  const node = doc.layers[id];
  if (!node) return doc;
  const { parentId, index } = opts;

  if (parentId !== null) {
    const newParent = doc.layers[parentId];
    if (!newParent || newParent.type !== 'group') {
      throw new Error(`moveNode: parent "${parentId}" is not a group`);
    }
    // Cycle check: new parent must not be `id` or any descendant of `id`.
    if (parentId === id) throw new Error('moveNode: cannot move a layer into itself');
    const descendants = collectDescendantIds(doc, id);
    if (descendants.has(parentId)) {
      throw new Error('moveNode: cannot move a layer into its own descendant');
    }
  }

  // Detach from old parent.
  const layers = { ...doc.layers };
  let rootIds = doc.rootIds;
  if (node.parentId === null) {
    rootIds = doc.rootIds.filter((rid) => rid !== id);
  } else {
    const oldParent = layers[node.parentId];
    if (oldParent && oldParent.type === 'group') {
      layers[node.parentId] = {
        ...oldParent,
        childIds: (oldParent.childIds ?? []).filter((c) => c !== id),
      };
    }
  }

  // Re-attach to new parent.
  const moved: LayerNode = { ...node, parentId };
  layers[id] = moved;

  if (parentId === null) {
    rootIds = insertAt(rootIds, id, index);
  } else {
    const newParent = layers[parentId];
    if (newParent && newParent.type === 'group') {
      layers[parentId] = {
        ...newParent,
        childIds: insertAt(newParent.childIds ?? [], id, index),
      };
    }
  }

  return { rootIds, layers };
}

/** Set or clear the mask on a layer. Pass `null` to detach.
 *  Same no-op-on-unknown-id contract as `updateNode`. */
export function setMask(doc: EditorDocument, id: string, mask: LayerMask | null): EditorDocument {
  const node = doc.layers[id];
  if (!node) return doc;
  // Distinguish between "explicitly cleared" and "field absent".
  // Setting to undefined keeps the node shape consistent — both
  // null and missing mean "no mask" downstream.
  const next: LayerNode = { ...node, mask: mask ?? undefined };
  return { ...doc, layers: { ...doc.layers, [id]: next } };
}

interface GroupOptions {
  /** Display name for the new group. Defaults to 'Group'. */
  name?: string;
  /** Explicit id for the new group. Defaults to a synthesized
   *  string. Tests pass an explicit id; runtime callers usually
   *  let the helper synthesize. */
  id?: string;
}

/** Wrap a contiguous list of sibling layers in a new group node.
 *  All ids must currently share the same `parentId` (root or the
 *  same group). The new group lands at the position of the topmost
 *  affected layer in the parent's child list, taking ownership of
 *  the listed children in their original order.
 *
 *  Throws when:
 *    - any id is missing
 *    - the ids don't share a parent
 *    - `ids` is empty
 *
 *  Returns a fresh document with the group inserted. */
export function groupNodes(
  doc: EditorDocument,
  ids: string[],
  opts: GroupOptions = {},
): EditorDocument {
  if (ids.length === 0) throw new Error('groupNodes: no ids supplied');
  // Validate every id exists and shares one parent.
  let sharedParent: string | null | undefined = undefined;
  for (const id of ids) {
    const node = doc.layers[id];
    if (!node) throw new Error(`groupNodes: layer "${id}" not found`);
    if (sharedParent === undefined) sharedParent = node.parentId;
    else if (sharedParent !== node.parentId) {
      throw new Error('groupNodes: ids must share a single parent');
    }
  }
  const parentId = sharedParent ?? null;
  const siblings = parentId === null ? doc.rootIds : (doc.layers[parentId]?.childIds ?? []);
  // Preserve original sibling order for the new group's children
  // — callers might pass ids in selection order, but the visual
  // tree should follow document order.
  const idSet = new Set(ids);
  const orderedChildren = siblings.filter((sid) => idSet.has(sid));
  const insertIndex = siblings.findIndex((sid) => idSet.has(sid));

  const groupId = opts.id ?? `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  if (doc.layers[groupId]) throw new Error(`groupNodes: id "${groupId}" already exists`);

  const groupNode: LayerNode = {
    id: groupId,
    name: opts.name ?? 'Group',
    type: 'group',
    visible: true,
    parentId,
    childIds: orderedChildren,
  };

  // Re-parent the group's children, drop them from the old parent's
  // ordered list, insert the group at the topmost child's position.
  const layers: Record<string, LayerNode> = {};
  for (const [k, v] of Object.entries(doc.layers)) {
    if (idSet.has(k)) {
      layers[k] = { ...v, parentId: groupId };
    } else {
      layers[k] = v;
    }
  }
  layers[groupId] = groupNode;

  if (parentId === null) {
    const remaining = doc.rootIds.filter((sid) => !idSet.has(sid));
    const rootIds = [...remaining.slice(0, insertIndex), groupId, ...remaining.slice(insertIndex)];
    return { rootIds, layers };
  }

  const oldParent = doc.layers[parentId];
  if (!oldParent || oldParent.type !== 'group') {
    throw new Error(`groupNodes: parent "${parentId}" is not a group`);
  }
  const remaining = (oldParent.childIds ?? []).filter((sid) => !idSet.has(sid));
  const updatedParent: LayerNode = {
    ...oldParent,
    childIds: [...remaining.slice(0, insertIndex), groupId, ...remaining.slice(insertIndex)],
  };
  layers[parentId] = updatedParent;
  return { rootIds: doc.rootIds, layers };
}

/** Inverse of `groupNodes`: flatten a group's children back into
 *  the group's parent at the group's position, then delete the
 *  group node. The children's relative order is preserved.
 *
 *  Returns the original document if the id is missing or doesn't
 *  refer to a group. */
export function ungroupNode(doc: EditorDocument, id: string): EditorDocument {
  const node = doc.layers[id];
  if (!node || node.type !== 'group') return doc;

  const childIds = node.childIds ?? [];
  const parentId = node.parentId;

  // Re-parent children to the group's parent.
  const layers: Record<string, LayerNode> = {};
  for (const [k, v] of Object.entries(doc.layers)) {
    if (k === id) continue; // drop the group node
    if (childIds.includes(k)) {
      layers[k] = { ...v, parentId };
    } else {
      layers[k] = v;
    }
  }

  if (parentId === null) {
    const insertIndex = doc.rootIds.indexOf(id);
    const rootIds = [
      ...doc.rootIds.slice(0, insertIndex),
      ...childIds,
      ...doc.rootIds.slice(insertIndex + 1),
    ];
    return { rootIds, layers };
  }

  const oldParent = doc.layers[parentId];
  if (!oldParent || oldParent.type !== 'group') {
    // Parent vanished — degenerate case. Return as-is rather than
    // crash; this branch is unreachable when the doc is well-
    // formed but worth handling defensively.
    return doc;
  }
  const insertIndex = (oldParent.childIds ?? []).indexOf(id);
  const newChildIds = [
    ...(oldParent.childIds ?? []).slice(0, insertIndex),
    ...childIds,
    ...(oldParent.childIds ?? []).slice(insertIndex + 1),
  ];
  layers[parentId] = { ...oldParent, childIds: newChildIds };
  return { rootIds: doc.rootIds, layers };
}

/** Apply a partial patch to a single node. Returns the original
 *  document if the id is missing — no-op rather than throw, since
 *  reducer flows commonly fire updates against ids that were just
 *  removed. */
export function updateNode(
  doc: EditorDocument,
  id: string,
  patch: Partial<LayerNode>,
): EditorDocument {
  const node = doc.layers[id];
  if (!node) return doc;
  return {
    ...doc,
    layers: { ...doc.layers, [id]: { ...node, ...patch } },
  };
}

/** Reorder via a (fromIndex, toIndex) pair operating on the
 *  flattened ordered list. Used to map the Tier 0 reducer's
 *  REORDER_LAYER action onto tree-aware moves while the LayersPanel
 *  hasn't yet been redesigned to think in (parentId, index). Both
 *  indices are bounded into [0, len-1]; out-of-range or matching
 *  indices return the document unchanged. */
export function reorderFlattened(
  doc: EditorDocument,
  fromIndex: number,
  toIndex: number,
): EditorDocument {
  const ordered = toOrderedList(doc);
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return doc;
  if (fromIndex >= ordered.length || toIndex >= ordered.length) return doc;
  // Tier 0 reorders only the root list; we mirror that here so we
  // don't accidentally lift a layer out of a group it never lived
  // in. Group-aware reorder is the LayersPanel rewrite's job.
  const id = ordered[fromIndex].id;
  const node = doc.layers[id];
  if (!node || node.parentId !== null) return doc;
  const targetId = ordered[toIndex].id;
  const targetNode = doc.layers[targetId];
  if (!targetNode || targetNode.parentId !== null) return doc;

  const targetRootIndex = doc.rootIds.indexOf(targetId);
  if (targetRootIndex === -1) return doc;
  return moveNode(doc, id, { parentId: null, index: targetRootIndex });
}

// ── internals ────────────────────────────────────────────────────

/** Insert `id` at `index` in `arr`, returning a new array. Indices
 *  out of range clamp to [0, length]. */
function insertAt<T>(arr: readonly T[], id: T, index: number): T[] {
  const i = Math.max(0, Math.min(index, arr.length));
  return [...arr.slice(0, i), id, ...arr.slice(i)];
}

/** Recursively walk the subtree rooted at `id` and return every
 *  descendant id (NOT including `id` itself). Used for cycle
 *  detection in `moveNode` and bulk-removal in `removeNode`. */
function collectDescendantIds(doc: EditorDocument, id: string): Set<string> {
  const out = new Set<string>();
  const stack = [id];
  while (stack.length > 0) {
    const cur = stack.pop()!;
    const node = doc.layers[cur];
    if (!node || node.type !== 'group' || !node.childIds) continue;
    for (const cid of node.childIds) {
      if (out.has(cid)) continue;
      out.add(cid);
      stack.push(cid);
    }
  }
  return out;
}

/** Atomic merge primitive: insert `mergedLayer` at the position of
 *  the topmost replaced layer (lowest index within its parent's
 *  child list), then remove every node listed in `replacedIds`
 *  along with its descendants. The replaced ids MUST all share the
 *  same parent — the caller guarantees this (the EditorPage handler
 *  filters by `parentId === activeParent` before dispatching) and
 *  the helper throws otherwise so the bug surfaces in tests.
 *
 *  Rationale for "topmost position": Photoshop semantics. The
 *  merged layer compositionally replaces the selected stack; if it
 *  drifted to front-of-root it would re-order over unrelated layers
 *  and change the visible composition. */
export function replaceLayersWithMerged(
  doc: EditorDocument,
  mergedLayer: LayerNode,
  replacedIds: readonly string[],
): EditorDocument {
  if (replacedIds.length === 0) {
    throw new Error('replaceLayersWithMerged: replacedIds must be non-empty');
  }
  if (doc.layers[mergedLayer.id]) {
    throw new Error(`replaceLayersWithMerged: merged id "${mergedLayer.id}" already exists`);
  }
  // Resolve the shared parent. All replaced layers must agree —
  // anything else is a caller bug.
  const first = doc.layers[replacedIds[0]];
  if (!first) {
    throw new Error(`replaceLayersWithMerged: id "${replacedIds[0]}" not found`);
  }
  const parentId = first.parentId;
  for (const id of replacedIds) {
    const node = doc.layers[id];
    if (!node) throw new Error(`replaceLayersWithMerged: id "${id}" not found`);
    if (node.parentId !== parentId) {
      throw new Error('replaceLayersWithMerged: all replaced ids must share one parent');
    }
  }
  // Topmost = lowest index in the parent's child list.
  const siblingList =
    parentId === null ? doc.rootIds : ((doc.layers[parentId]?.childIds ?? []) as readonly string[]);
  let topmostIndex = siblingList.length;
  for (const id of replacedIds) {
    const idx = siblingList.indexOf(id);
    if (idx >= 0 && idx < topmostIndex) topmostIndex = idx;
  }
  // Insert merged at topmostIndex BEFORE removing the others so
  // subsequent removes can't shift our target. Then remove each
  // replaced id (cascading descendants — matches the existing
  // `removeNode` contract). The final position of `mergedLayer` in
  // the parent's child list equals `topmostIndex` because every
  // removed id was at index ≥ topmostIndex (we just inserted at the
  // smallest one), so removals only affect indices to its right.
  let next = addNode(doc, { ...mergedLayer, parentId }, { parentId, index: topmostIndex });
  for (const id of replacedIds) {
    next = removeNode(next, id);
  }
  return next;
}
